import { FocusEvent, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useMeasure } from 'react-use';

import { EMAIL, WHATS_APP } from 'constants/messageMediums';
import { WhatsAppLocaleContent } from 'modules/templateEditor/common/types';
import { getMemberData } from 'modules/templatePicker/ducks/selectors';
import { TemplatePreview } from 'modules/templatePreview';
import { SearchableText } from 'ui-beta/searchableText';
import noop from 'utils/noop';

import { selectIsWhatsAppInfobipLocalesEnabled } from 'core/userContext';
import { AllMessageMedium, CampaignType } from 'modules/domains/campaign';
import { TemplateType } from 'modules/templateEditor/common';
import { Ellipse } from 'ui/icons/other/Ellipse';
import { getDisplayTemplateType } from '../utils';
import { LocalesPopover } from './LocalesPopover';
import { MemberDetails } from './MemberDetails';
import {
  DetailsContainer,
  DetailsLabelExtended,
  Flex,
  PreviewContainer,
  StyledBasicFilterSelect,
  StyledCard,
  StyledTitleWrapper,
  StyledTooltip,
} from './TemplateTilesListItem.styled';

type TemplateTilesListItemProps = {
  campaignType?: CampaignType;
  isSelected: boolean;
  messageMedium?: AllMessageMedium;
  name: string;
  onClick: () => void;
  onFocus: (e: FocusEvent<HTMLDivElement>) => void;
  searchText?: string;
  templateId: number;
  updatedAt?: string;
  updatedBy?: string;
  updatedFromNow?: string;
  url?: string;
  memberId: number;
  hasError?: boolean;
  dataFeeds?: { id: number; name: string }[];
  onSelectDataFeed: (dataFeedId: number | null) => void;
  templateType: TemplateType;
  locales?: string[];
  whatsAppLocaleContent?: WhatsAppLocaleContent[];
};

export const TemplateTilesListItem = ({
  campaignType,
  isSelected = false,
  messageMedium = EMAIL,
  name: templateName,
  onClick = noop,
  onFocus = noop,
  searchText = '',
  templateId,
  updatedAt,
  updatedFromNow,
  memberId,
  hasError = false,
  dataFeeds = [],
  onSelectDataFeed = noop,
  templateType,
  whatsAppLocaleContent = [],
}: TemplateTilesListItemProps) => {
  const isWhatsAppGA = useSelector(selectIsWhatsAppInfobipLocalesEnabled);
  const [selectedDataFeedId, setSelectedDataFeed] = useState<number | null>(null);
  const { firstName, lastName, avatarUrl } = useSelector((state) =>
    getMemberData(state, { memberId }),
  );
  const hasDataFeeds = dataFeeds.length > 0;
  const titleRef = useRef<HTMLDivElement>(null);
  const [labelRef] = useMeasure<HTMLDivElement>();

  const handleSelectDataFeed = (dataFeedId: number | null) => {
    setSelectedDataFeed(dataFeedId);
    onSelectDataFeed(dataFeedId);
  };

  const onSelectTile = () => {
    handleSelectDataFeed(null);
    onClick();
  };
  const isTitleTruncated =
    !!titleRef.current && titleRef.current?.offsetWidth < titleRef.current?.scrollWidth;

  const dataFeedsOptions = useMemo(
    () =>
      dataFeeds.map(({ id, name }) => ({
        id: id.toString(),
        value: id.toString(),
        label: name,
        isSelected: id?.toString() === selectedDataFeedId?.toString(),
      })),
    [dataFeeds],
  );

  const displayTemplateType = getDisplayTemplateType({ messageMedium, campaignType, templateType });

  return (
    <StyledCard
      data-test="templates-wizard--list-item"
      isSelected={isSelected} // used for styling needs
      hasError={hasError}
      onClick={onSelectTile}
      onFocus={onFocus}
      role="button"
      tabIndex="0"
      id={`template-tile-${templateId}`}
    >
      <PreviewContainer>
        <TemplatePreview
          templateId={templateId}
          messageMedium={messageMedium}
          whatsAppLocaleContent={whatsAppLocaleContent}
        />
      </PreviewContainer>
      <DetailsContainer>
        <StyledTooltip isEnabled={isTitleTruncated} tooltipContent={templateName}>
          <DetailsLabelExtended ref={labelRef}>
            <StyledTitleWrapper ref={titleRef}>
              <SearchableText isCaseSensitive={false} text={templateName} searchText={searchText} />
            </StyledTitleWrapper>
          </DetailsLabelExtended>
        </StyledTooltip>

        {isSelected && hasDataFeeds ? (
          <StyledBasicFilterSelect
            data-test="data-feeds-dropdown"
            id="data-feeds-dropdown"
            isRequired
            onSelect={(selected) => handleSelectDataFeed(Number(selected[0]))}
            options={dataFeedsOptions}
            placeholder="Select data feed..."
            isFullWidth={true}
          />
        ) : (
          <Flex>
            <MemberDetails
              avatarUrl={avatarUrl}
              firstName={firstName || ''}
              lastName={lastName || ''}
              memberId={memberId}
              displayTemplateType={displayTemplateType}
              updatedFromNow={updatedFromNow}
              updatedAt={updatedAt}
            />
            {messageMedium === WHATS_APP && isWhatsAppGA && !!whatsAppLocaleContent?.length && (
              <>
                <Ellipse width={3} height={3} />
                <LocalesPopover whatsAppLocaleContent={whatsAppLocaleContent} />
              </>
            )}
          </Flex>
        )}
      </DetailsContainer>
    </StyledCard>
  );
};
